import { sortBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React from 'react';

import { convertUnits, convertEntityQuantityToInpulseUnit } from '@commons/utils/conversion';
import { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';
import { formatNumber } from '@commons/DisplayNumber';
import { TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import Text from '@commons/Text';

import {
  renderCategoryCell,
  renderNameCell,
  renderStandardHeader,
} from '../../../../forecast/Forecast/SalesPlanning/datagrid/customCellRenders';

import { getEntityTotalColumn } from './shared/getEntityTotalColumn';

import { EntityTimeSlotHeaderContainer, EntityTimeSlotCellContainer } from './styledComponents';

const formatTime = (time) =>
  moment(time, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE_SECOND).format(
    TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE,
  );

// Simple header component for entity timeslots (display only)
const EntityTimeslotHeader = ({ timeSlot }) => (
  <EntityTimeSlotHeaderContainer>
    <Text
      color={ENUM_COLORS.DARKEST}
      font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}
      style={{
        marginBottom: '2px',
      }}
    >
      {timeSlot.name}
    </Text>
    <Text color={ENUM_COLORS.DARK} font={ENUM_FONTS.TEXT_SMALL}>
      {formatTime(timeSlot.startTime)}-{formatTime(timeSlot.endTime)}
    </Text>
  </EntityTimeSlotHeaderContainer>
);

// Simple display cell for entity timeslot data
const EntityTimeslotCell = ({ row, timeSlotId }) => {
  const toProduce = row.dataByTimeSlotIds?.[timeSlotId]?.toProduce || 0;

  const convertedUnit = convertUnits(row.unit, true, toProduce);
  const convertedToProduce = formatNumber(
    convertEntityQuantityToInpulseUnit(toProduce < 0 ? 0 : toProduce, row.unit),
    2,
  );

  if (row.isIngredient || !row.batchProduction) {
    return (
      <EntityTimeSlotCellContainer>
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_BOLD}>
          {convertedToProduce} {convertedUnit}
        </Text>
      </EntityTimeSlotCellContainer>
    );
  }

  const convertedQuantity = formatNumber(
    convertEntityQuantityToInpulseUnit(row.unitQuantity, row.unit),
    2,
  );

  return (
    <EntityTimeSlotCellContainer>
      <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_BOLD}>
        {convertedToProduce} {convertedUnit}
      </Text>
      <Text color={ENUM_COLORS.IP_BLACK_5} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>{`${formatNumber(
        convertedToProduce / convertedQuantity,
        2,
      )} x ${convertedQuantity} ${convertedUnit}`}</Text>
    </EntityTimeSlotCellContainer>
  );
};

// Simple view column for entities (display toProduce for single timeslot)
const getEntitySimpleViewColumn = (singleTimeslotId, commonColumnsProps) => ({
  ...commonColumnsProps,
  field: 'entityData',
  headerName: i18next.t('PRODUCTION.PLANNING.TO_PRODUCE'),
  flex: 1,
  headerAlign: 'center',
  align: 'center',
  sortable: false,
  resizable: false,
  renderCell: (params) => {
    const { row } = params;
    const toProduce = row.dataByTimeSlotIds?.[singleTimeslotId]?.toProduce || 0;

    const convertedUnit = convertUnits(row.unit, true, toProduce);
    const convertedToProduce = formatNumber(
      convertEntityQuantityToInpulseUnit(toProduce, row.unit),
      2,
    );

    if (row.isIngredient || !row.batchProduction) {
      return (
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_BOLD}>
          {convertedToProduce} {convertedUnit}
        </Text>
      );
    }

    const convertedQuantity = formatNumber(
      convertEntityQuantityToInpulseUnit(row.unitQuantity, row.unit),
      2,
    );

    return (
      <EntityTimeSlotCellContainer>
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_BOLD}>
          {convertedToProduce} {convertedUnit}
        </Text>
        <Text
          color={ENUM_COLORS.IP_BLACK_5}
          font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}
        >{`${formatNumber(
          convertedToProduce / convertedQuantity,
          2,
        )} x ${convertedQuantity} ${convertedUnit}`}</Text>
      </EntityTimeSlotCellContainer>
    );
  },
});

// Generate timeslot columns for entities (display only)
const generateEntityTimeslotColumns = (timeSlots) => {
  const sortedTimeSlots = sortBy(timeSlots, 'startTime');

  return sortedTimeSlots.map((timeSlot) => ({
    field: `entity_timeSlot_${timeSlot.id}`,
    headerName: '',
    minWidth: 200,
    flex: 1,
    disableColumnMenu: true,
    sortable: false,
    filterable: false,
    disableReorder: true,
    headerAlign: 'center',
    resizable: false,
    renderHeader: () => <EntityTimeslotHeader timeSlot={timeSlot} />,
    renderCell: (params) => <EntityTimeslotCell row={params.row} timeSlotId={timeSlot.id} />,
  }));
};

// Shared columns for entities (name and category)
const getEntitySharedColumns = (commonColumnsProps) => {
  const nameColumn = {
    ...commonColumnsProps,
    field: 'name',
    headerName: i18next.t('GENERAL.NAME'),
    minWidth: 320,
    flex: 0,
    sortable: true,
    resizable: false,
  };

  const categoryColumn = {
    ...commonColumnsProps,
    field: 'category',
    headerName: i18next.t('GENERAL.CATEGORY'),
    minWidth: 200,
    flex: 0,
    renderCell: ({ row }) => renderCategoryCell(row.category),
    filterable: true,
    sortable: true,
    resizable: false,
    getApplyQuickFilterFn: (value) => {
      if (!value) {
        return () => true;
      }

      return (params) => {
        const categoryValue = params.value;
        const searchValue = value.toLowerCase();

        if (!categoryValue) {
          const translatedNoneValue = i18next.t('GENERAL.NONE_VALUE').toLowerCase();
          return translatedNoneValue.includes(searchValue);
        }

        return categoryValue.toLowerCase().includes(searchValue);
      };
    },
  };

  return { nameColumn, categoryColumn };
};

export const getEntitiesColumns = (type, timeSlots = []) => {
  // Determine if we should use simple view (single timeslot without name or no timeslots)
  const useSimpleView = timeSlots.length === 1 && !timeSlots[0].name;

  // Get the single timeslot ID for simple view
  const singleTimeslotId = timeSlots.length === 1 ? timeSlots[0].id : null;

  const commonColumnsProps = {
    disableReorder: true,
    disableColumnMenu: true,
    renderHeader: renderStandardHeader,
  };

  const { nameColumn, categoryColumn } = getEntitySharedColumns(commonColumnsProps);

  // Set appropriate renderCell for name column based on entity type
  nameColumn['renderCell'] = ({ row }) =>
    renderNameCell(row.name, type, { withImg: true, imgSrc: row.img });

  if (useSimpleView) {
    // Simple view: show single column for toProduce
    const simpleViewColumn = getEntitySimpleViewColumn(singleTimeslotId, commonColumnsProps);

    return [nameColumn, categoryColumn, simpleViewColumn];
  } else {
    // Multi-timeslot view: generate timeslot columns
    const timeSlotColumns = generateEntityTimeslotColumns(timeSlots);

    return [
      nameColumn,
      categoryColumn,
      ...timeSlotColumns,
      getEntityTotalColumn(commonColumnsProps),
    ];
  }
};
